import React, { useEffect, useState } from 'react'
import IconButton from '@material-ui/core/IconButton';
import IntlMessages from '@crema/utility/IntlMessages';
import Grid from '@material-ui/core/Grid';
import { Form, Formik } from 'formik';
import FormikTextField from 'components/Fields/FormikTextField';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles } from '@material-ui/core';
import { CremaTheme } from 'types/crema/AppContextPropsType';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { deityListThunk, selectCurrentTempleDeities } from 'store/reducers/temple/deitySlice';
import { offeringCategoryListThunk, selectCurrentTempleOfferingCategories } from 'store/reducers/temple/offeringCategorySlice';
import * as yup from 'yup'
import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/Refresh';
import PaginatedToolbar from 'components/Table/PaginatedTable/PaginatedToolbar';

interface OfferingTagToolbarProps {
    changeFilter: (filters: OfferingTagFilterForm) => void,
    values: OfferingTagFilterForm,
    addNew?: () => void
}

const useStyles = makeStyles((theme: CremaTheme) => (
    createStyles({
        field: {
            flexGrow: 1
        },
        fieldRoot: {
            width: '100%'
        },
        formGridRoot: {
            [theme.breakpoints.down('sm')]: {
                flexDirection: "column"
            },
        }
    })
))

export type OfferingTagFilterForm = {
    deity: string,
    category: string
}

const OfferingTagToolbar: React.FC<OfferingTagToolbarProps> = ({ changeFilter, values, addNew }) => {
    const [showFilters, setShowFilters] = useState(false);
    const handleShowFilter = () => setShowFilters(!showFilters)
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const deities = useAppSelector(selectCurrentTempleDeities)
    const categories = useAppSelector(selectCurrentTempleOfferingCategories)

    useEffect(() => {
        if (deities.length === 0) {
            dispatch(deityListThunk({}))
        }
        if (categories.length === 0) {
            dispatch(offeringCategoryListThunk({}))
        }
    }, [dispatch, deities.length, categories.length])

    const validationSchema = yup.object({
        deity: yup.string(),
        category: yup.string(),
    })

    return (
        <PaginatedToolbar 
            titleId="offeringTag.title" 
            handleShowFilter={handleShowFilter} 
            showFilters={showFilters} 
            addNew={addNew} 
        >
            <Formik
                initialValues={values}
                validationSchema={validationSchema}
                onSubmit={(values) => changeFilter(values)}
            >
                {({ isValid, resetForm }) => (
                    <Form noValidate autoComplete="off">
                        <Grid container direction="row" spacing={6} className={classes.formGridRoot}>
                            <Grid item container md={4} xs={12} spacing={2}>
                                <Grid item className={classes.field}>
                                    <FormikTextField
                                        select
                                        name='deity'
                                        className={classes.fieldRoot}
                                        size="small"
                                        label={<IntlMessages id='deity' />}
                                        margin="none"
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {deities.map(deity => (
                                            <MenuItem key={deity.id} value={deity.url}>
                                                {deity.name}
                                            </MenuItem>
                                        ))}
                                    </FormikTextField>
                                </Grid>
                            </Grid>
                            <Grid item container md={4} xs={12} spacing={2}>
                                <Grid item className={classes.field}>
                                    <FormikTextField
                                        select
                                        name='category'
                                        className={classes.fieldRoot}
                                        size="small"
                                        label={<IntlMessages id='common.category' />}
                                        margin="none"
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {categories.map(category => (
                                            <MenuItem key={category.id} value={category.url}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </FormikTextField>
                                </Grid>
                            </Grid>
                            <Grid item container spacing={4} xs={4} alignItems='flex-end'>
                                <Grid item>
                                    <Button disabled={!isValid} variant="contained" size="small" color="primary" type="submit">
                                        <IntlMessages id="common.apply" />
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <IconButton onClick={() => {
                                        changeFilter({ category: '', deity: '' })
                                        resetForm()
                                    }} size="small">
                                        <RefreshIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </PaginatedToolbar>
    );
};

export default React.memo(OfferingTagToolbar)
